import { EventEmitter, Injectable, OnDestroy } from '@angular/core';
import { ConfirmationModalServiceConfig, ModalOptions } from '@models/modal.model';
import { NgbModal, NgbModalRef, NgbOffcanvas, NgbOffcanvasRef } from '@ng-bootstrap/ng-bootstrap';
import { ConfirmationModalComponent } from '@shared-components/confirmation-modal/confirmation-modal.component';
import { Subscription } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ModalService implements OnDestroy {
  private active_instances: NgbModalRef[] = [];
  private subscriptions: Subscription = new Subscription();

  constructor(private ngb_modal_svc: NgbModal, private ngb_offcanvas_svc: NgbOffcanvas) {
    this.subscriptions.add(
      this.ngb_modal_svc.activeInstances.subscribe((activeInstances: NgbModalRef[]) => {
        this.active_instances = activeInstances;
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get activeInstances(): EventEmitter<NgbModalRef[]> {
    return this.ngb_modal_svc.activeInstances;
  }

  open(content: any, options: ModalOptions): NgbModalRef {
    const { onShow, onClose, onDismiss, ...modal_options } = options;
    const modal: NgbModalRef = this.ngb_modal_svc.open(content, modal_options);

    if (onShow) {
      const sub = modal.shown.subscribe(() => {
        sub.unsubscribe();
        onShow();
      });
    }

    return modal;
  }

  openOffCanvas(content: any): NgbOffcanvasRef {
    const offcanvas: NgbOffcanvasRef = this.ngb_offcanvas_svc.open(content);

    return offcanvas;
  }

  dismissAll(reason?: any): void {
    this.ngb_modal_svc.dismissAll(reason);
  }

  hasOpenModals(): boolean {
    return this.ngb_modal_svc.hasOpenModals();
  }

  transitionToModal(content: any, options: ModalOptions = {}): void {
    const { onClose, onDismiss, ...modal_options } = options;

    if (this.hasOpenModals()) {
      const modal: NgbModalRef = this.active_instances[this.active_instances.length - 1];
      const sub: Subscription = modal.hidden.subscribe(() => {
        sub.unsubscribe();
        onClose?.();
        this.open(content, modal_options);
      });
      modal.close();
    } else {
      onClose?.();
      this.open(content, modal_options);
    }
  }

  dismissLastModal(reason?: any, cb?: () => void): void {
    if (!this.hasOpenModals()) return;

    const modal: NgbModalRef = this.active_instances[this.active_instances.length - 1];

    if (!cb) return modal.dismiss(reason);

    const sub: Subscription = modal.hidden.subscribe(() => {
      sub.unsubscribe();
      cb();
    });

    modal.dismiss(reason);
  }

  confirmationDialog(
    config: ConfirmationModalServiceConfig,
    options: ModalOptions = {}
  ): NgbModalRef {
    const { onDismiss, onCancel, onConfirm, ...confirm_config } = config;
    const { onClose, ...modal_options } = options;
    const modal_ref: NgbModalRef = this.open(ConfirmationModalComponent, modal_options);
    const modal_content: ConfirmationModalComponent =
      modal_ref.componentInstance as ConfirmationModalComponent;

    modal_content.config = confirm_config;

    const action_event_sub: Subscription = modal_content.action_event.subscribe((event) => {
      action_event_sub.unsubscribe();

      switch (event.action) {
        case 'cancel':
          onCancel?.(event.data);
          modal_ref.close();
          break;

        case 'confirm':
          onConfirm?.(event.data);
          modal_ref.close();
          break;

        case 'dismiss':
          onDismiss?.(event.data);
          modal_ref.dismiss();
          break;
      }
    });

    const hidden_sub: Subscription = modal_ref.hidden.subscribe(() => {
      hidden_sub.unsubscribe();
      onClose?.();
    });

    return modal_ref;
  }
}
